import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Alert,
} from '@mui/material';
import { Search, PermIdentity, AccessTime, CalendarToday } from '@mui/icons-material';
import dayjs from 'dayjs';

import { AppDispatch } from '../../../../../../store';
import { fetchWorkerList } from '../../../../../../modules/worker/actions';
import { selectWorkerList } from '../../../../../../modules/worker/selectors';
import { IFacilityShift } from '../../../../../../modules/facilityShifts/types';
import { ActionButton, Container } from './styles';
import { assignWorkers, resetAssignWorkerState } from '../../../../../../modules/facilityShifts/actions';
import { selectAssignWorkersLoading, selectAssignWorkersSuccess, selectAssignWorkersError } from '../../../../../../modules/facilityShifts/selectors';
import { validateWorkerShiftTiming } from '../../../../../../modules/shift/actions';
import { IWorkerValidationResult } from '../../../../../../modules/shift/types';
import AlertPopup from '../../../../../../components/common/AlertPopup';
import SecureAvatar from 'components/common/SecureAvatar';

interface ManualAssignProps {
  shift: IFacilityShift;
  onCancel: () => void;
  onAssign: (selectedWorkerIds: number[]) => void;
}

const ManualAssign: React.FC<ManualAssignProps> = ({ shift, onCancel, onAssign }) => {
  const dispatch = useDispatch<AppDispatch>();
  const workers = useSelector(selectWorkerList);
  const loading = useSelector(selectAssignWorkersLoading);
  const success = useSelector(selectAssignWorkersSuccess);
  const error = useSelector(selectAssignWorkersError);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWorkers, setSelectedWorkers] = useState<number[]>([]);
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [validationData, setValidationData] = useState<IWorkerValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [alert, setAlert] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    dispatch(resetAssignWorkerState());
    return () => {
      dispatch(resetAssignWorkerState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      setAlert({
        open: true,
        message: 'Workers assigned successfully!',
        severity: 'success'
      });
      // Delay closing to let user see success message
      setTimeout(() => {
        onAssign(selectedWorkers);
      }, 1500);
    }
  }, [success, onCancel, onAssign, selectedWorkers]);

  useEffect(() => {
    if (error) {
      setAlert({
        open: true,
        message: typeof error === 'string' ? error : 'Failed to assign workers',
        severity: 'error'
      });
    }
  }, [error]);

  useEffect(() => {
    // Fetch users for this facility using the correct worker list API
    if (shift.facility) {
      dispatch(fetchWorkerList({
        page: 1,
        pageSize: 10,
        status: 'active',
        facilityId: typeof shift.facility === 'number' ? shift.facility : undefined, // Ensure ID is passed if it's a number, or handle string case if needed
        searchTerm: searchTerm || undefined
      }));
    }
  }, [dispatch, shift.facility, searchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleToggleWorker = (workerId: number) => {
    setSelectedWorkers(prev =>
      prev.includes(workerId)
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const shiftDuration = dayjs(shift.end_date_time).diff(dayjs(shift.start_date_time), 'hour', true);

  // The 'workers' from simple selector is IWorker[]. 
  // Filtering is done via API (searchTerm), but we can also filter client side if needed/preferred or for immediate feedback.
  // The user requested explicit "limit 5" in the logic previously, but API pagination (pageSize: 10) controls it now.
  // We can just render the list returned by API (up to 10). Or slice it to 5 if strictly required. 
  // Let's slice to 5 to match the specific requirement "limit to 5".

  const displayWorkers = (workers || [])
    .filter(w => !(shift as any).assigned_workers_details?.some((aw: any) => aw.id === w.worker_id || aw.worker_id === w.worker_id))
    .slice(0, 5);

  const proceedAssign = () => {
    if (shift.shift_id && selectedWorkers.length > 0) {
      dispatch(assignWorkers({
        shift_id: shift.shift_id,
        worker_ids: selectedWorkers
      }));
      setValidationModalOpen(false);
    }
  };

  const handleAssignClick = async () => {
    if (!shift.shift_id || selectedWorkers.length === 0) return;

    setIsValidating(true);
    try {
      const response = await dispatch(validateWorkerShiftTiming({
        shift_id: shift.shift_id,
        worker_ids: selectedWorkers
      })).unwrap();

      if (response.status && response.data) {
        const failures = response.data.filter((r: IWorkerValidationResult) => r.validation_failures && r.validation_failures.length > 0);
        if (failures.length > 0) {
          setValidationData(failures);
          setValidationModalOpen(true);
        } else {
          proceedAssign();
        }
      } else {
        proceedAssign();
      }
    } catch (err) {
      // In case of error validating, maybe we want to just proceed or alert?
      // Let's proceed for now or just log it
      console.error("Validation failed", err);
      proceedAssign();
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Container>
      {/* Shift Summary Header */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarToday sx={{ fontSize: 16, color: '#6B7280' }} />
            <Typography variant="body2" color="textSecondary" fontWeight={500}>
              {dayjs(shift.start_date_time).format('DD/MM/YYYY')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <PermIdentity sx={{ fontSize: 18, color: '#6B7280' }} />
            <Typography variant="body2" color="textSecondary" fontWeight={500}>
              {shift.position_role} ({shift.shift_assignee_count}/{shift.workers_required})
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTime sx={{ fontSize: 16, color: '#6B7280' }} />
          <Typography variant="body2" color="textSecondary">
            {dayjs(shift.start_date_time).format('HH.mm')} - {dayjs(shift.end_date_time).format('HH.mm')} • {shiftDuration} hours
          </Typography>
        </Box>
      </Box>

      {/* Available Workers Section */}
      <Typography variant="subtitle1" fontWeight={700} mb={2}>
        Available Workers
      </Typography>

      <TextField
        fullWidth
        placeholder="Search worker"
        value={searchTerm}
        onChange={handleSearchChange}
        size="small"
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: '#9CA3AF' }} />
            </InputAdornment>
          ),
        }}
      />

      <Box display="flex" flexDirection="column" gap={2} mb={3}>
        {displayWorkers.map((worker) => (
          <Box
            key={worker.worker_id}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Checkbox
                checked={selectedWorkers.includes(worker.worker_id)}
                onChange={() => handleToggleWorker(worker.worker_id)}
                sx={{ p: 0, '&.Mui-checked': { color: '#52786F' } }}
              />
              <SecureAvatar
                src={worker.user.profile_picture || undefined}
                sx={{ width: 40, height: 40 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={600} color="#111827">
                  {worker.user.first_name} {worker.user.last_name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="textSecondary">
                    {worker.position || 'Worker'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">•</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Rating 5.0 {/* Placeholder */}
                  </Typography>
                  <Rating value={5} readOnly size="small" max={1} sx={{ fontSize: '14px' }} />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
        {displayWorkers.length === 0 && (
          <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
            No available workers found.
          </Typography>
        )}
      </Box>

      {/* Actions */}
      <Box display="flex" flexDirection="column" gap={1.5}>
        <ActionButton
          variant="contained"
          colorType="green"
          fullWidth
          onClick={handleAssignClick}
          disabled={selectedWorkers.length === 0 || loading || isValidating}
        >
          {loading ? 'Assigning...' : 'Assign Worker'}
        </ActionButton>
        <ActionButton
          variant="outlined"
          colorType="green"
          fullWidth
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </ActionButton>
      </Box>

      <AlertPopup
        open={alert.open}
        onClose={() => setAlert(prev => ({ ...prev, open: false }))}
        message={alert.message}
        severity={alert.severity}
      />

      <Dialog
        open={validationModalOpen}
        onClose={() => setValidationModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
          Validation Warnings
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>
            The following workers have validation issues with this shift. Do you still want to assign them?
          </Typography>
          <List>
            {validationData.map((workerResult) => (
              <ListItem key={workerResult.worker_id} sx={{ flexDirection: 'column', alignItems: 'flex-start', borderBottom: '1px solid #eee', py: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {workerResult.worker_name}
                </Typography>
                <Box mt={0.5}>
                  {workerResult.validation_failures.map((failure, idx) => (
                    <Alert severity="warning" key={idx} sx={{ py: 0, px: 1, mb: 0.5, '& .MuiAlert-icon': { py: 0.2 } }}>
                      <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                        {failure.message}
                      </Typography>
                    </Alert>
                  ))}
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={proceedAssign} color="error" variant="contained">
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManualAssign;
